/**
 * Karaoke word timing — pure math for the word-by-word caption highlight
 * (CapCut-parity phase 2, 2026-07-19).
 *
 * True per-word timestamps do not exist for TRANSLATED captions: Whisper
 * times the ENGLISH source words, and word order does not survive
 * translation. So the karaoke clock is a LENGTH-WEIGHTED spread of the
 * cue's window across its visible words — each word owns a slice of
 * [startMs, endMs) proportional to its visible glyph count (min weight 1
 * so a one-letter word still gets a beat). Deterministic, pure function
 * of the text + window: preview and render agree by construction, and it
 * works for every language including RTL.
 *
 * PURE module — no React, no DOM. Imported by the composition
 * (CaptionLine) and unit-tested under root vitest.
 */

export interface WordWindow {
  /** Offset from cue start, ms (inclusive). */
  startMs: number;
  /** Offset from cue start, ms (exclusive). */
  endMs: number;
}

/** Visible-length weight of one word (≥1 so zero-width never divides). */
function weight(word: string): number {
  return Math.max(1, [...word].length);
}

/**
 * Length-weighted windows for `words` across a cue lasting `durationMs`.
 * Guarantees full coverage: windows abut exactly and the last one ends at
 * durationMs (no float gap — the tail is clamped).
 */
export function wordWindows(words: string[], durationMs: number): WordWindow[] {
  if (words.length === 0 || durationMs <= 0) return [];
  const weights = words.map(weight);
  const total = weights.reduce((a, b) => a + b, 0);
  const out: WordWindow[] = [];
  let cursor = 0;
  for (let i = 0; i < words.length; i++) {
    const span = (weights[i]! / total) * durationMs;
    const end = i === words.length - 1 ? durationMs : cursor + span;
    out.push({ startMs: cursor, endMs: end });
    cursor = end;
  }
  return out;
}

/**
 * Index of the word active at `tMs` (offset from cue start). Before the
 * first window → 0; at/after the end → the last index. Never -1 for a
 * non-empty list, so the highlight always sits on a real word.
 */
export function activeWordIndex(windows: WordWindow[], tMs: number): number {
  if (windows.length === 0) return -1;
  if (tMs < windows[0]!.startMs) return 0;
  for (let i = 0; i < windows.length; i++) {
    if (tMs < windows[i]!.endMs) return i;
  }
  return windows.length - 1;
}

/**
 * 0..1 progress inside word `idx` at `tMs` — drives the "pop" scale.
 * Clamped; a zero-length window reads as finished.
 */
export function wordProgress(windows: WordWindow[], idx: number, tMs: number): number {
  const w = windows[idx];
  if (!w) return 1;
  const span = w.endMs - w.startMs;
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, (tMs - w.startMs) / span));
}
